import React from "react";
import { Link, useNavigate } from "react-router-dom";

export default function NotFound() {
  const navigate = useNavigate();

  return (
    <>
      <style>{`
        .not-found-page {
          width: 100%;
          min-height: calc(100vh - 110px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 32px 20px;
          box-sizing: border-box;
          background:
            radial-gradient(
              circle at top left,
              rgba(37, 99, 235, 0.08),
              transparent 32%
            ),
            radial-gradient(
              circle at bottom right,
              rgba(14, 165, 233, 0.08),
              transparent 30%
            ),
            #f8fafc;
        }

        .not-found-card {
          position: relative;
          width: 100%;
          max-width: 620px;
          padding: 52px 42px 36px;
          box-sizing: border-box;
          text-align: center;
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 24px;
          box-shadow:
            0 24px 60px rgba(15, 23, 42, 0.08),
            0 6px 20px rgba(15, 23, 42, 0.04);
          overflow: hidden;
        }

        .not-found-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 50%;
          width: 180px;
          height: 4px;
          border-radius: 0 0 20px 20px;
          background: linear-gradient(90deg, #2563eb, #0ea5e9);
          transform: translateX(-50%);
        }

        .not-found-icon {
          width: 84px;
          height: 84px;
          margin: 0 auto 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2563eb;
          background: linear-gradient(
            145deg,
            rgba(37, 99, 235, 0.12),
            rgba(14, 165, 233, 0.06)
          );
          border: 1px solid rgba(37, 99, 235, 0.12);
          border-radius: 24px;
        }

        .not-found-code {
          margin-bottom: 6px;
          font-size: clamp(64px, 10vw, 96px);
          font-weight: 800;
          line-height: 1;
          letter-spacing: -5px;
          color: transparent;
          background: linear-gradient(135deg, #1d4ed8, #0ea5e9);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .not-found-title {
          margin: 0;
          color: #0f172a;
          font-size: clamp(26px, 4vw, 34px);
          font-weight: 750;
          line-height: 1.25;
          letter-spacing: -0.5px;
        }

        .not-found-description {
          max-width: 470px;
          margin: 16px auto 0;
          color: #64748b;
          font-size: 15.5px;
          line-height: 1.7;
        }

        .not-found-actions {
          margin-top: 30px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }

        .not-found-button {
          min-height: 46px;
          padding: 0 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          box-sizing: border-box;
          border-radius: 10px;
          font-family: inherit;
          font-size: 14px;
          font-weight: 650;
          line-height: 1;
          text-decoration: none;
          cursor: pointer;
          transition:
            transform 0.2s ease,
            box-shadow 0.2s ease,
            border-color 0.2s ease,
            background-color 0.2s ease;
        }

        .not-found-button:hover {
          transform: translateY(-2px);
        }

        .not-found-button:focus-visible {
          outline: 3px solid rgba(37, 99, 235, 0.2);
          outline-offset: 3px;
        }

        .not-found-button-primary {
          color: #ffffff;
          background: linear-gradient(135deg, #2563eb, #0284c7);
          border: 1px solid transparent;
          box-shadow: 0 10px 24px rgba(37, 99, 235, 0.2);
        }

        .not-found-button-primary:hover {
          color: #ffffff;
          box-shadow: 0 14px 30px rgba(37, 99, 235, 0.28);
        }

        .not-found-button-secondary {
          color: #334155;
          background: #ffffff;
          border: 1px solid #dbe2ea;
        }

        .not-found-button-secondary:hover {
          color: #0f172a;
          background: #f8fafc;
          border-color: #cbd5e1;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.07);
        }

        .not-found-footer {
          margin-top: 34px;
          padding-top: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 9px;
          color: #94a3b8;
          font-size: 13px;
          border-top: 1px solid #eef2f7;
        }

        .not-found-footer-dot {
          width: 7px;
          height: 7px;
          flex-shrink: 0;
          background: #22c55e;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.1);
        }

        @media (max-width: 640px) {
          .not-found-page {
            min-height: calc(100vh - 80px);
            padding: 20px 14px;
          }

          .not-found-card {
            padding: 38px 20px 28px;
            border-radius: 18px;
          }

          .not-found-icon {
            width: 72px;
            height: 72px;
            border-radius: 20px;
          }

          .not-found-actions {
            flex-direction: column-reverse;
          }

          .not-found-button {
            width: 100%;
          }

          .not-found-footer {
            line-height: 1.5;
          }
        }
      `}</style>

      <main className="not-found-page">
        <section className="not-found-card">
          <div className="not-found-icon" aria-hidden="true">
            <svg
              width="42"
              height="42"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.5 3.75H6.75C5.09315 3.75 3.75 5.09315 3.75 6.75V17.25C3.75 18.9069 5.09315 20.25 6.75 20.25H17.25C18.9069 20.25 20.25 18.9069 20.25 17.25V13.5"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              <path
                d="M14.25 3.75H20.25V9.75"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              <path
                d="M20.25 3.75L11.25 12.75"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <div className="not-found-code">404</div>

          <h1 className="not-found-title">Page not found</h1>

          <p className="not-found-description">
            The page you are looking for does not exist, may have been moved,
            or the URL may be incorrect.
          </p>

          <div className="not-found-actions">
            <button
              type="button"
              className="not-found-button not-found-button-secondary"
              onClick={() => navigate(-1)}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M15 18L9 12L15 6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              Go Back
            </button>

            <Link
              to="/"
              className="not-found-button not-found-button-primary"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M3 10.75L12 3L21 10.75V20C21 20.5523 20.5523 21 20 21H15V15H9V21H4C3.44772 21 3 20.5523 3 20V10.75Z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              Go to Dashboard
            </Link>
          </div>

          <div className="not-found-footer">
            <span className="not-found-footer-dot" />
            Please check the address and try again.
          </div>
        </section>
      </main>
    </>
  );
}